<template>
  <component
    :is="componentName"
    v-bind="elementAttrs"
    :class="[themeClasses, defaultClasses]"
    @click="$emit('click', $event)"
  >
    <span class="flex-1 text-center flex items-center justify-center">
      <mdicon
        v-if="icon && iconPosition == 'start'"
        :class="['inline-flex relative', props.rounded ? '' : 'mr-1']"
        :aria-label="icon"
        :name="icon"
      />
      <slot></slot>
      <mdicon
        v-if="icon && iconPosition == 'end'"
        :class="['inline-flex relative', props.rounded ? '' : 'ml-1']"
        :aria-label="icon"
        :name="icon"
      />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    href?: string
    icon?: string
    iconPosition?: 'start' | 'end'
    rounded?: boolean
    expanded?: boolean
    theme?: 'labour' | 'text' | 'secondary' | 'tertiary' | 'primary' | 'inherit'
    disabled?: boolean
    to?: string
  }>(),
  {
    href: '',
    icon: '',
    iconPosition: 'end',
    rounded: false,
    expanded: false,
    theme: 'labour',
    disabled: false,
    to: '',
  }
)

defineEmits<{
  click: [event: MouseEvent]
}>()

const componentName = computed(() => {
  if (props.disabled) {
    return 'button'
  }

  if (props.to) {
    return 'router-link'
  }

  if (props.href) {
    return 'a'
  }

  return 'button'
})

const elementAttrs = computed(() => {
  switch (componentName.value) {
    case 'router-link':
      return { to: props.to }
    case 'a':
      return { href: props.href }
    default:
      return { disabled: props.disabled }
  }
})

const defaultClasses = computed(() => {
  let classes =
    'transition-all inline-flex font-normal rounded-full items-center'

  if (props.disabled) {
    classes += ' cursor-not-allowed'
  }

  if (props.expanded) {
    classes += ' py-2 px-4'
  } else if (props.rounded) {
    classes += ' p-2'
  } else {
    classes += ' py-1 px-4'
  }

  return classes
})

const themeClasses = computed(() => {
  switch (props.theme) {
    case 'text':
      return 'bg-transparent text-red-1 hover:text-red-2 active:text-red-3 disabled:opacity-60 dark:text-dark-red dark:hover:text-red-5'
    case 'secondary':
      return 'bg-grey-9 text-black hover:bg-grey-8 active:bg-grey-7 disabled:bg-grey-10  disabled:opacity-60 dark:bg-dark-surface-2 dark:text-dark-ink dark:hover:bg-dark-border dark:active:bg-dark-border dark:disabled:bg-dark-surface'
    case 'tertiary':
      return 'bg-white text-black hover:bg-grey-9 active:bg-grey-8 disabled:bg-grey-10  disabled:opacity-60 dark:bg-dark-surface dark:text-dark-ink dark:hover:bg-dark-surface-2 dark:active:bg-dark-border dark:disabled:bg-dark-surface'
    case 'primary':
      return 'bg-black text-white hover:bg-grey-2 active:bg-grey-3 disabled:bg-grey-11 disabled:text-grey-14 dark:bg-dark-ink dark:text-dark-bg dark:hover:bg-grey-8 dark:active:bg-grey-7'
    case 'inherit':
      return '!px-0 !py-0'
    case 'labour':
    default:
      return 'bg-red-1 text-white hover:bg-red-2 active:bg-red-3 disabled:bg-pink-2 disabled:text-grey-14'
  }
})
</script>
